import React, { useCallback, useMemo, useState } from 'react';
import axios from 'axios';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import InfoPopupV2 from 'components/Popups/InfoPopupv2';
import { patchSubscriptionCollectionMethodRequest } from 'services/subscriptions';
import { getCurrentUserSubscription } from 'store/actionCreators/subscriptions';
import { UserSubscription } from 'types';
import { toast as addAppToast } from 'utils/toastAbstraction';

import { SwitchBillingModeError, TextLink } from './styled';

type SwitchBillingModeLinkProps = {
  subscription: UserSubscription;
  billingPortalUrl: string | null;
};

const SwitchBillingModeLink = ({
  subscription,
  billingPortalUrl,
}: SwitchBillingModeLinkProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [needsPaymentMethod, setNeedsPaymentMethod] = useState(false);

  const isInvoiceBilling =
    subscription.stripeCollectionMethod === 'send_invoice';
  const targetMethod = isInvoiceBilling ? 'card' : 'invoice';

  const modalCopy = useMemo(() => {
    if (isInvoiceBilling) {
      return {
        title: t('subscriptions.profile.payment.switchToCardTitle'),
        message: t('subscriptions.profile.payment.switchToCardBody'),
        bold: t('subscriptions.profile.payment.switchToCardBodyBold'),
        confirm: t('subscriptions.profile.payment.switchToCardConfirm'),
        link: t('subscriptions.profile.payment.switchToCard'),
      };
    }
    return {
      title: t('subscriptions.profile.payment.switchToInvoiceTitle'),
      message: t('subscriptions.profile.payment.switchToInvoiceBody'),
      bold: t('subscriptions.profile.payment.switchToInvoiceBodyBold'),
      confirm: t('subscriptions.profile.payment.switchToInvoiceConfirm'),
      link: t('subscriptions.profile.payment.switchToInvoice'),
    };
  }, [isInvoiceBilling, t]);

  const openModal = useCallback(() => {
    setInlineError(null);
    setNeedsPaymentMethod(false);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    if (submitting) return;
    setModalOpen(false);
  }, [submitting]);

  const handleConfirm = useCallback(async () => {
    if (submitting) return;
    setInlineError(null);
    setNeedsPaymentMethod(false);
    setSubmitting(true);
    try {
      await patchSubscriptionCollectionMethodRequest({ method: targetMethod });
      addAppToast(
        { title: t('notifications.billing_mode_updated') },
        {
          appearance: 'success',
          autoDismiss: true,
        },
      );
      dispatch(getCurrentUserSubscription.trigger());
    } catch (err) {
      const apiData = axios.isAxiosError(err)
        ? (err.response?.data as
            | { message?: string; error?: string }
            | undefined)
        : undefined;
      const code = apiData?.error?.toUpperCase() ?? '';
      const message = apiData?.message?.toLowerCase() ?? '';
      const isNoPaymentMethod =
        code.includes('PAYMENT_METHOD') ||
        code.includes('NO_CARD') ||
        message.includes('payment method');

      if (isNoPaymentMethod) {
        setNeedsPaymentMethod(true);
        setInlineError(
          t('subscriptions.profile.payment.switchBillingNoPaymentMethod'),
        );
      } else {
        setInlineError(
          t('subscriptions.profile.payment.switchBillingErrorGeneric'),
        );
      }
    } finally {
      setSubmitting(false);
    }
  }, [dispatch, submitting, t, targetMethod]);

  return (
    <>
      <div>
        <TextLink
          as="button"
          type="button"
          onClick={openModal}
          style={{
            fontSize: 11,
            marginTop: 6,
            padding: 0,
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            textAlign: 'left',
            display: 'inline-block',
          }}
        >
          {modalCopy.link}
        </TextLink>
        {inlineError ? (
          <SwitchBillingModeError role="alert">
            {inlineError}
            {needsPaymentMethod && billingPortalUrl ? (
              <>
                {' '}
                <Trans
                  i18nKey="subscriptions.profile.payment.switchBillingNoPaymentMethodLink"
                  components={{
                    link: (
                      <TextLink
                        href={billingPortalUrl}
                        target="_blank"
                        rel="noreferrer"
                      />
                    ),
                  }}
                />
              </>
            ) : null}
          </SwitchBillingModeError>
        ) : null}
      </div>

      <InfoPopupV2
        isOpen={modalOpen}
        type="warning"
        shortHeight
        headerText={modalCopy.title}
        messageText={modalCopy.message}
        boldMessageText={modalCopy.bold}
        twoButtons
        cancelButtonText={t('common.cancel')}
        okButtonText={modalCopy.confirm}
        onClose={closeModal}
        onConfirm={handleConfirm}
      />
    </>
  );
};

export default SwitchBillingModeLink;
